import { useCallback, useMemo } from 'react';
import type {
  SuppressionCondition,
  SuppressionConfig,
  SuppressionRule,
  SuppressionRuleType,
} from '../../types/pipelineConfig';
import { getDefaultSuppressionConfig, getPostNormalizationFieldSuggestions } from '../../utils/pipelineDefaults';
import { TimeZoneSelect } from '../../utils/timezones';
import { FieldPathInput } from './FieldPathInput';
import { StepConfigHeader } from './StepConfigHeader';
import { StrategySelector } from './StrategySelector';
import type { StrategyOption } from './StrategySelector';
import { TimeWindowInput } from './TimeWindowInput';

interface SuppressionConfigEditorProps {
  config?: SuppressionConfig;
  onChange: (config: SuppressionConfig) => void;
  endpointType: string | number;
  disabled?: boolean;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
}

const RULE_TYPE_OPTIONS: StrategyOption<SuppressionRuleType>[] = [
  { value: 'timeWindow', label: 'Time Window', icon: '⏱️', description: 'Suppress duplicates within a window.' },
  { value: 'maintenance', label: 'Maintenance', icon: '🛠️', description: 'Suppress during maintenance periods.' },
  { value: 'dependency', label: 'Dependency', icon: '🔗', description: 'Suppress children when parent fails.' },
  { value: 'threshold', label: 'Threshold', icon: '📉', description: 'Suppress unless threshold is exceeded.' },
];

function createId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

const EMPTY_CONDITION: SuppressionCondition = {
  field: '',
  operator: 'equals',
  value: '',
};

const CRON_PRESETS = [
  { value: '0 0 * * *', label: 'Midnight (00:00)' },
  { value: '0 1 * * *', label: '01:00' },
  { value: '0 2 * * *', label: '02:00' },
  { value: '0 3 * * *', label: '03:00' },
  { value: '0 4 * * *', label: '04:00' },
  { value: '0 5 * * *', label: '05:00' },
  { value: '0 6 * * *', label: '06:00' },
  { value: '0 7 * * *', label: '07:00' },
  { value: '0 8 * * *', label: '08:00' },
  { value: '0 9 * * *', label: '09:00' },
  { value: '0 17 * * *', label: '17:00' },
  { value: '0 18 * * *', label: '18:00' },
  { value: '0 20 * * *', label: '20:00' },
  { value: '0 22 * * *', label: '22:00' },
  { value: '0 0 * * 1-5', label: 'Midnight (weekdays only)' },
  { value: '0 6 * * 1-5', label: '06:00 (weekdays only)' },
  { value: '0 18 * * 1-5', label: '18:00 (weekdays only)' },
  { value: '0 22 * * 0,6', label: '22:00 (weekends only)' },
] as const;

export function SuppressionConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  disabled = false,
  isSystemDefault = false,
  hideToggle,
}: SuppressionConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultSuppressionConfig(endpointType), [endpointType]);
  const config = useMemo<SuppressionConfig>(() => ({
    ...defaultConfig,
    ...(rawConfig ?? {}),
    rules: rawConfig?.rules ?? defaultConfig.rules ?? [],
  }), [defaultConfig, rawConfig]);
  const isStepEnabled = (config as SuppressionConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const suggestions = useMemo(() => getPostNormalizationFieldSuggestions(), []);

  const updateConfig = useCallback((patch: Partial<SuppressionConfig>) => {
    onChange({ ...config, ...patch });
  }, [config, onChange]);

  const updateRule = useCallback((id: string, patch: Partial<SuppressionRule>) => {
    updateConfig({ rules: config.rules.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  }, [config.rules, updateConfig]);

  const addRule = useCallback(() => {
    updateConfig({
      rules: [
        ...config.rules,
        {
          id: createId('supp'),
          name: 'New suppression rule',
          type: 'timeWindow',
          enabled: true,
          conditions: [{ ...EMPTY_CONDITION }],
          timeWindowMinutes: 15,
        },
      ],
    });
  }, [config.rules, updateConfig]);

  const removeRule = useCallback((id: string) => {
    updateConfig({ rules: config.rules.filter((r) => r.id !== id) });
  }, [config.rules, updateConfig]);

  const addCondition = useCallback((ruleId: string) => {
    const target = config.rules.find((r) => r.id === ruleId);
    if (!target) return;
    updateRule(ruleId, { conditions: [...target.conditions, { ...EMPTY_CONDITION }] });
  }, [config.rules, updateRule]);

  const updateCondition = useCallback((
    ruleId: string,
    index: number,
    patch: Partial<SuppressionCondition>,
  ) => {
    const target = config.rules.find((r) => r.id === ruleId);
    if (!target) return;
    updateRule(ruleId, {
      conditions: target.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
  }, [config.rules, updateRule]);

  const removeCondition = useCallback((ruleId: string, index: number) => {
    const target = config.rules.find((r) => r.id === ruleId);
    if (!target) return;
    updateRule(ruleId, { conditions: target.conditions.filter((_, i) => i !== index) });
  }, [config.rules, updateRule]);

  const updateMaintenanceField = useCallback((
    ruleId: string,
    fieldName: 'startCron' | 'endCron' | 'timezone',
    value: string,
  ) => {
    const target = config.rules.find((r) => r.id === ruleId);
    if (!target) return;

    const current = target.maintenanceSchedule;
    updateRule(ruleId, {
      maintenanceSchedule: {
        startCron: current?.startCron ?? '0 0 * * *',
        endCron: current?.endCron ?? '0 6 * * *',
        timezone: current?.timezone ?? 'UTC',
        [fieldName]: value,
      },
    });
  }, [config.rules, updateRule]);

  return (
    <div className="space-y-4">
      <StepConfigHeader
        title="Suppression"
        isEnabled={isStepEnabled}
        onToggleEnabled={(enabled) =>
          onChange({ ...config, isEnabled: enabled } as SuppressionConfig & { isEnabled: boolean })
        }
        isSystemDefault={isSystemDefault}
        disabled={disabled}
        hideToggle={hideToggle}
      />
      <div className={isStepEnabled ? 'space-y-4' : 'space-y-4 opacity-50 pointer-events-none'}>
      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" aria-label="Suppression global settings">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>⚙️ Global Settings</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{config.globalSuppressionWindowMinutes}m</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div className="space-y-3">
            <TimeWindowInput
              value={config.globalSuppressionWindowMinutes}
              onChange={(globalSuppressionWindowMinutes) => updateConfig({ globalSuppressionWindowMinutes })}
              disabled={disabled}
              label="Global Suppression Window"
            />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={config.enableMaintenanceWindows}
                onChange={(e) => updateConfig({ enableMaintenanceWindows: e.target.checked })}
                disabled={disabled}
                className="h-4 w-4 rounded"
              />
              Enable maintenance windows
            </label>
          </div>
        </div>
      </details>

      <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
          <span>📋 Suppression Rules</span>
          <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{config.rules.length}</span>
        </summary>
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div className="space-y-3">
          {config.rules.map((rule) => (
            <details key={rule.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <span className="truncate">
                  {RULE_TYPE_OPTIONS.find((option) => option.value === rule.type)?.icon ?? '📋'} {rule.name || '(unnamed)'}
                </span>
                <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{rule.conditions?.length ?? 0} conditions</span>
              </summary>
              <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    className="input flex-1"
                  value={rule.name}
                  onChange={(e) => updateRule(rule.id, { name: e.target.value })}
                  disabled={disabled}
                  placeholder="Rule name"
                />
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={(e) => updateRule(rule.id, { enabled: e.target.checked })}
                    disabled={disabled}
                    className="h-4 w-4 rounded"
                  />
                  Enabled
                </label>
                <button type="button" onClick={() => removeRule(rule.id)} disabled={disabled} className="text-sm text-red-600 dark:text-red-400">Remove</button>
              </div>

              <StrategySelector
                options={RULE_TYPE_OPTIONS}
                value={rule.type}
                onChange={(type) => updateRule(rule.id, { type })}
                disabled={disabled || !rule.enabled}
              />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Conditions</h4>
                  <button
                    type="button"
                    onClick={() => addCondition(rule.id)}
                    disabled={disabled || !rule.enabled}
                    className="text-xs font-medium text-blue-600 dark:text-blue-400"
                  >
                    + Add Condition
                  </button>
                </div>
                {rule.conditions.map((condition, index) => (
                  <div key={`${rule.id}-${index}`} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <FieldPathInput
                      value={condition.field}
                      onChange={(field) => updateCondition(rule.id, index, { field })}
                      suggestions={suggestions}
                      disabled={disabled || !rule.enabled}
                    />
                    <select
                      className="input"
                      value={condition.operator}
                      onChange={(e) => updateCondition(rule.id, index, { operator: e.target.value as SuppressionCondition['operator'] })}
                      disabled={disabled || !rule.enabled}
                    >
                      <option value="equals">equals</option>
                      <option value="contains">contains</option>
                      <option value="regex">regex</option>
                      <option value="exists">exists</option>
                    </select>
                    <input
                      className="input"
                      value={condition.value}
                      onChange={(e) => updateCondition(rule.id, index, { value: e.target.value })}
                      disabled={disabled || !rule.enabled || condition.operator === 'exists'}
                      placeholder="Value"
                    />
                    <button type="button" onClick={() => removeCondition(rule.id, index)} disabled={disabled || !rule.enabled} className="btn-secondary text-sm">
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              {rule.type === 'timeWindow' && (
                <TimeWindowInput
                  value={rule.timeWindowMinutes ?? 15}
                  onChange={(timeWindowMinutes) => updateRule(rule.id, { timeWindowMinutes })}
                  disabled={disabled || !rule.enabled}
                  label="Time Window (minutes)"
                />
              )}

              {rule.type === 'maintenance' && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Maintenance Window</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Schedule periods when alerts should be suppressed. Select start and end times and timezone.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <div className="text-xs text-gray-600 dark:text-gray-400">Start schedule</div>
                      {(() => {
                        const startCron = rule.maintenanceSchedule?.startCron ?? '0 0 * * *';
                        const isStartPreset = CRON_PRESETS.some((p) => p.value === startCron);
                        return (
                          <>
                            <select
                              className="input"
                              value={isStartPreset ? startCron : '__custom__'}
                              onChange={(e) => {
                                if (e.target.value !== '__custom__') {
                                  updateMaintenanceField(rule.id, 'startCron', e.target.value);
                                }
                              }}
                              disabled={disabled || !rule.enabled}
                            >
                              {CRON_PRESETS.map((preset) => (
                                <option key={preset.value} value={preset.value}>
                                  {preset.label}
                                </option>
                              ))}
                              <option value="__custom__">Custom cron…</option>
                            </select>
                            {!isStartPreset && (
                              <input
                                type="text"
                                className="input mt-1"
                                value={startCron}
                                onChange={(e) => updateMaintenanceField(rule.id, 'startCron', e.target.value)}
                                placeholder="Custom cron expression"
                                disabled={disabled || !rule.enabled}
                              />
                            )}
                          </>
                        );
                      })()}
                    </div>

                    <div className="space-y-1">
                      <div className="text-xs text-gray-600 dark:text-gray-400">End schedule</div>
                      {(() => {
                        const endCron = rule.maintenanceSchedule?.endCron ?? '0 6 * * *';
                        const isEndPreset = CRON_PRESETS.some((p) => p.value === endCron);
                        return (
                          <>
                            <select
                              className="input"
                              value={isEndPreset ? endCron : '__custom__'}
                              onChange={(e) => {
                                if (e.target.value !== '__custom__') {
                                  updateMaintenanceField(rule.id, 'endCron', e.target.value);
                                }
                              }}
                              disabled={disabled || !rule.enabled}
                            >
                              {CRON_PRESETS.map((preset) => (
                                <option key={preset.value} value={preset.value}>
                                  {preset.label}
                                </option>
                              ))}
                              <option value="__custom__">Custom cron…</option>
                            </select>
                            {!isEndPreset && (
                              <input
                                type="text"
                                className="input mt-1"
                                value={endCron}
                                onChange={(e) => updateMaintenanceField(rule.id, 'endCron', e.target.value)}
                                placeholder="Custom cron expression"
                                disabled={disabled || !rule.enabled}
                              />
                            )}
                          </>
                        );
                      })()}
                    </div>

                    <div className="space-y-1">
                      <div className="text-xs text-gray-600 dark:text-gray-400">Timezone</div>
                      <TimeZoneSelect
                        value={rule.maintenanceSchedule?.timezone ?? 'UTC'}
                        onChange={(timezone) => updateMaintenanceField(rule.id, 'timezone', timezone)}
                        className="w-full"
                        disabled={disabled || !rule.enabled}
                      />
                    </div>
                  </div>
                </div>
              )}

              {rule.type === 'dependency' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldPathInput
                    value={rule.parentField ?? ''}
                    onChange={(parentField) => updateRule(rule.id, { parentField })}
                    suggestions={suggestions}
                    disabled={disabled || !rule.enabled}
                    placeholder="Parent field"
                  />
                  <FieldPathInput
                    value={rule.childField ?? ''}
                    onChange={(childField) => updateRule(rule.id, { childField })}
                    suggestions={suggestions}
                    disabled={disabled || !rule.enabled}
                    placeholder="Child field"
                  />
                </div>
              )}

                {rule.type === 'threshold' && (
                  <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Threshold</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Suppress after N matching alerts within a time window to prevent alert storms.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="number"
                      min={1}
                      className="input"
                      value={rule.thresholdCount ?? 10}
                      onChange={(e) => updateRule(rule.id, { thresholdCount: Number(e.target.value) || 1 })}
                      disabled={disabled || !rule.enabled}
                      placeholder="Threshold count"
                    />
                    <TimeWindowInput
                      value={rule.thresholdWindowMinutes ?? 10}
                      onChange={(thresholdWindowMinutes) => updateRule(rule.id, { thresholdWindowMinutes })}
                      disabled={disabled || !rule.enabled}
                      label="Threshold Window"
                    />
                  </div>
                  </div>
                )}
                </div>
              </div>
            </details>
          ))}

          <button type="button" onClick={addRule} disabled={disabled} className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400">
            + Add Suppression Rule
          </button>
          </div>
        </div>
      </details>
      </div>
    </div>
  );
}
